import React, { useEffect, useState } from 'react';
import { CHAPTER_STATE_EVENT, OPEN_CHAPTER_EVENT, TOGGLE_CHAPTER_EVENT } from './ChapterFold';

// Desktop: one row of coloured chapter tiles — a table of contents for the folded
// chapters below. Clicking a tile opens its chapter and scrolls to it;
// clicking an open chapter's tile closes it again. Hidden on phones, which
// use the chapter cards instead.

export interface ChapterInfo {
  id: string;
  number: string;
  meta: string;
  title: string;
  summary: string;
  image: string;
}

const ChapterIndex: React.FC<{ chapters: ChapterInfo[] }> = ({ chapters }) => {
  const [openIds, setOpenIds] = useState<string[]>([]);

  useEffect(() => {
    const onState = (event: Event) => {
      const { id, open } = (event as CustomEvent<{ id: string; open: boolean }>).detail ?? {};
      if (!id) return;
      setOpenIds((ids) => (open ? Array.from(new Set([...ids, id])) : ids.filter((x) => x !== id)));
    };
    window.addEventListener(CHAPTER_STATE_EVENT, onState);
    return () => window.removeEventListener(CHAPTER_STATE_EVENT, onState);
  }, []);

  const choose = (id: string) => {
    if (openIds.includes(id)) {
      window.dispatchEvent(new CustomEvent(TOGGLE_CHAPTER_EVENT, { detail: { id } }));
      return;
    }
    window.dispatchEvent(new CustomEvent(OPEN_CHAPTER_EVENT, { detail: { id } }));
    const el = document.getElementById(id);
    if (el) window.scrollTo({ top: Math.max(0, el.offsetTop - 64), behavior: 'smooth' });
  };

  return (
    <nav className="chapter-index" aria-labelledby="chapter-index-title">
      <div className="chapter-index-inner">
        <p className="ed-eyebrow">Five chapters · open one at a time</p>
        <h2 id="chapter-index-title">The <em>guide</em></h2>
        <ul>
          {chapters.map((c) => {
            const isOpen = openIds.includes(c.id);
            return (
              <li key={c.id}>
                <button
                  type="button"
                  className={isOpen ? 'is-open' : ''}
                  aria-expanded={isOpen}
                  aria-controls={c.id}
                  onClick={() => choose(c.id)}
                >
                  <span className="chapter-index-num" aria-hidden="true">{c.number}</span>
                  <small>{c.meta}</small>
                  <strong>
                    {c.title}
                    <span aria-hidden="true">{isOpen ? ' ↑' : ' ↓'}</span>
                  </strong>
                  <span className="chapter-index-summary">{c.summary}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
};

export default ChapterIndex;
